import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  Copy,
  Check,
  Send,
  Loader2,
  Layers,
  ArrowLeft,
  Link2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';

/**
 * Build & Send Review — Document 2 · Phase 6.2
 *
 * Strict DRAFT-ONLY surface:
 *   - Renders deck preview + dual-tone email drafts produced by Phase 6.1 orchestrator
 *   - Reuses the DM-zero warning + override flow from Phase 5
 *   - "Send" buttons NEVER send — they navigate to /email-designer with prefilled fields
 *   - Deck "Open in Editor" navigates to /vision-decks/:id/edit (never auto-publishes)
 *
 * Data sources:
 *   - Live: GET /api/build-and-send/workflows/:workflowId  (after orchestrator runs)
 *   - Mock: ?mock=1 query param renders representative static data for design review
 */

// ==================== MOCK ====================

const MOCK_WORKFLOW = {
  id: 'mock-preview',
  workflow_id: 'mock-preview',
  deck_id: 'mock-deck-id',
  edit_url: '/vision-decks/mock-deck-id/edit',
  public_deck_url: '/decks/public/mock-deck-id',
  public_deck_status: 'draft_placeholder',
  page_count: 7,
  mode_used: 'template',
  context: {
    signal_type: 'ownership_change',
    property_type: 'Hospitality',
    location: 'Tampa',
    stage: 'qualified',
    coverage_count: 0, // triggers DM-zero warning in mock
    primary_dm: null,
  },
  warnings: ['dm_coverage_zero'],
  daily_usage: { used_today: 12, cap: 100, soft_warn: false },
  is_draft: true,
  requires_user_confirmation: true,
  generated_at: new Date().toISOString(),
  version_a: {
    tone: 'direct_confident',
    subject: 'New ownership at Cintas Tampa — quick uniform review',
    body:
      "Hi {{first_name}},\n\nNew-ownership transitions typically mean a 60-day window where uniform programs get re-scoped — fit, brand consistency, and supplier terms all surface fast.\n\nWe've helped three Florida hospitality groups stand up new programs in under six weeks without service interruption.\n\nWorth a 20-minute call to compare notes? I can share a one-page benchmark from the most recent rollout.\n\n[View the proposal: /decks/public/mock-deck-id]\n\n— {{sender_name}}",
    cta_url: '/decks/public/mock-deck-id',
  },
  version_b: {
    tone: 'warm_consultative',
    subject: 'Curious how Tampa transition is going?',
    body:
      "Hi {{first_name}},\n\nSaw the ownership change — congrats on the new chapter. A peer in Orlando ran into the same uniform-program crossroads last fall and found the supplier audit took longer than expected.\n\nNo pitch — just happy to share what worked (and what didn't). Sample report or 20-min call, your call.\n\n[View the proposal: /decks/public/mock-deck-id]\n\n— {{sender_name}}",
    cta_url: '/decks/public/mock-deck-id',
  },
};

// ==================== TONE CARD ====================

function ToneCard({ version, testidPrefix, locked, ctaUrl, onUse, onCopy, copied }) {
  const [subject, setSubject] = useState(version.subject || '');
  const [body, setBody] = useState(version.body || '');

  useEffect(() => {
    setSubject(version.subject || '');
    setBody(version.body || '');
  }, [version.subject, version.body]);

  const labelTokens =
    version.tone === 'direct_confident'
      ? { ring: 'ring-stone-200', accent: 'text-stone-700', icon: '🎯' }
      : { ring: 'ring-amber-200', accent: 'text-amber-800', icon: '🤝' };

  const label =
    version.tone === 'direct_confident' ? 'Direct & Confident' : 'Warm & Consultative';

  return (
    <div
      className={`rounded-sm border bg-white p-4 ring-2 ${labelTokens.ring}`}
      data-testid={`${testidPrefix}-card`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base" aria-hidden="true">{labelTokens.icon}</span>
          <h4 className={`text-sm font-semibold ${labelTokens.accent}`}>{label}</h4>
        </div>
        <Badge
          variant="outline"
          className="text-[10px] uppercase tracking-wider font-semibold"
        >
          {(version.tone || '').replace(/_/g, ' ')}
        </Badge>
      </div>

      <label className="block text-[11px] uppercase tracking-wide font-semibold text-stone-500 mb-1">
        Subject
      </label>
      <Input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="text-sm mb-3"
        data-testid={`${testidPrefix}-subject`}
      />

      <label className="block text-[11px] uppercase tracking-wide font-semibold text-stone-500 mb-1">
        Body
      </label>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={11}
        className="text-sm font-mono leading-relaxed mb-2"
        data-testid={`${testidPrefix}-body`}
      />

      {ctaUrl && (
        <div
          className="mb-3 flex items-center gap-2 px-2 py-1.5 rounded-sm border border-amber-200 bg-amber-50/60 text-[11px] text-amber-900"
          data-testid={`${testidPrefix}-cta-hint`}
        >
          <Link2 className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">
            CTA placeholder: <span className="font-mono">{ctaUrl}</span> · activates when deck is published
          </span>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => onUse({ subject, body, tone: version.tone })}
          disabled={locked}
          data-testid={`${testidPrefix}-send`}
          className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white disabled:bg-stone-300 disabled:text-stone-500"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Send (open in Email Designer)
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onCopy({ subject, body })}
          data-testid={`${testidPrefix}-copy`}
        >
          {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
          Copy
        </Button>
      </div>
    </div>
  );
}

// ==================== PAGE ====================

export default function BuildAndSendReview() {
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();

  const isMock = searchParams.get('mock') === '1' || workflowId === 'mock-preview';

  const [data, setData] = useState(isMock ? MOCK_WORKFLOW : null);
  const [loading, setLoading] = useState(!isMock);
  const [error, setError] = useState(null);
  const [overrideAcknowledged, setOverrideAcknowledged] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (isMock) return;
    if (!workflowId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/build-and-send/workflows/${workflowId}`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          const msg = err?.data?.detail || err?.message || 'Failed to load workflow';
          setError(typeof msg === 'string' ? msg : 'Failed to load workflow');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, isMock]);

  const warnings = data?.warnings || [];
  const context = data?.context || {};
  const locked =
    warnings.includes('dm_coverage_zero') && !overrideAcknowledged;

  const opportunityId = useMemo(() => data?.opportunity_id || null, [data]);

  const handleOverride = async () => {
    setOverrideAcknowledged(true);
    if (isMock || !opportunityId) return;
    try {
      await api.post('/sales-intelligence/outreach-email/override', {
        opportunity_id: opportunityId,
        reason: 'no_dm_coverage',
      });
    } catch {
      toast.error('Override logged locally but server-side audit failed.');
    }
  };

  const handleUse = (draft) => {
    const dm = (context && context.primary_dm) || {};
    const params = new URLSearchParams();
    if (draft.subject) params.set('subject', draft.subject);
    if (draft.body) params.set('body', draft.body);
    if (dm.email) params.set('to', dm.email);
    if (opportunityId) params.set('opportunity_id', opportunityId);
    params.set('from_outreach', '1');
    params.set('from_workflow', data?.id || data?.workflow_id || 'unknown');
    params.set('tone', draft.tone || '');
    navigate(`/email-designer?${params.toString()}`);
  };

  const handleCopy = async ({ subject, body }, key) => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // ignore
    }
  };

  const handleOpenDeck = () => {
    if (!data?.deck_id) return;
    navigate(`/vision-decks/${data.deck_id}/edit`);
  };

  // ==================== STATES ====================

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading workflow…</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <AlertTriangle className="h-8 w-8 text-amber-700 mx-auto" />
            <p className="text-sm font-semibold text-stone-800">Workflow unavailable</p>
            <p className="text-xs text-stone-500">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="bs-back-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F7]" data-testid="build-and-send-review">
      {/* ============ Header ============ */}
      <header className="border-b border-[#E8E6E1] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-sm hover:bg-stone-100 text-stone-600"
              data-testid="bs-header-back"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">
                Build &amp; Send · Review
              </p>
              <h1 className="text-base sm:text-lg font-semibold text-stone-900 truncate">
                Vision deck draft &amp; dual-tone outreach
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider font-semibold border-amber-300 text-amber-800 bg-amber-50"
              data-testid="bs-draft-badge"
            >
              <Sparkles className="h-2.5 w-2.5 mr-1" />
              Draft only
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* ============ Context Strip ============ */}
        <div
          className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-stone-500"
          data-testid="bs-context-strip"
        >
          <span>Context:</span>
          {context.signal_type && (
            <Badge variant="outline" className="text-[10px]">
              signal · {String(context.signal_type).replace(/_/g, ' ')}
            </Badge>
          )}
          {context.property_type && (
            <Badge variant="outline" className="text-[10px]">
              property · {context.property_type}
            </Badge>
          )}
          {context.location && (
            <Badge variant="outline" className="text-[10px]">
              location · {context.location}
            </Badge>
          )}
          {context.stage && (
            <Badge variant="outline" className="text-[10px]">
              stage · {context.stage}
            </Badge>
          )}
          <Badge variant="outline" className="text-[10px]">
            dm · {context.coverage_count ?? 0}
          </Badge>
          {data.daily_usage && (
            <Badge variant="outline" className="text-[10px]">
              {data.daily_usage.used_today}/{data.daily_usage.cap} today
            </Badge>
          )}
          {data.mode_used && (
            <Badge variant="outline" className="text-[10px]">
              mode · {data.mode_used}
            </Badge>
          )}
        </div>

        {/* ============ Deck Preview Card ============ */}
        <Card
          className="bg-white border-[#E8E6E1] overflow-hidden"
          data-testid="bs-deck-card"
        >
          <CardContent className="p-0">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 sm:items-center p-5">
              <div className="flex items-start gap-4 min-w-0">
                <div className="p-3 rounded-sm bg-[#B89B5E]/10 flex-shrink-0">
                  <Layers className="h-5 w-5 text-[#B89B5E]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">
                    Vision Deck draft
                  </p>
                  <h3 className="text-base font-semibold text-stone-900 truncate">
                    {data.deck_id ? `Deck · ${data.deck_id.slice(0, 8)}…` : 'Vision Deck'}
                  </h3>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {data.page_count ?? '—'} pages · template{' '}
                    <span className="font-mono text-stone-700">
                      {data.template_id || 'modern-executive'}
                    </span>{' '}
                    · status <span className="font-semibold text-amber-800">draft</span>
                  </p>
                  <p className="text-[11px] text-stone-500 mt-2 inline-flex items-center gap-1.5">
                    <Link2 className="h-3 w-3" />
                    Public URL{' '}
                    <span className="font-mono">{data.public_deck_url}</span>{' '}
                    <span className="text-amber-700 font-semibold">
                      · placeholder until you publish
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenDeck}
                  data-testid="bs-deck-open-btn"
                >
                  <Layers className="h-3.5 w-3.5 mr-1.5" />
                  Open in Editor
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ============ DM-zero Warning (reused from Phase 5) ============ */}
        {warnings.includes('dm_coverage_zero') && (
          <div
            className={`rounded-sm border p-4 transition-opacity ${
              overrideAcknowledged
                ? 'border-amber-200 bg-amber-50/40 opacity-70'
                : 'border-amber-300 bg-amber-50'
            }`}
            data-testid="bs-dm-coverage-warning"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  No decision makers identified yet
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Sending without a confirmed decision maker risks reaching the wrong inbox or
                  being filtered as spam.
                  {overrideAcknowledged && (
                    <span className="block mt-1 text-amber-900 font-medium">
                      Override acknowledged — drafts unlocked. Review carefully before sending.
                    </span>
                  )}
                </p>
                {!overrideAcknowledged && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        opportunityId
                          ? navigate(`/opportunity/${opportunityId}`)
                          : navigate(-1)
                      }
                      data-testid="bs-dm-add-cta"
                      className="border-amber-300 text-amber-900 hover:bg-amber-100"
                    >
                      Add decision maker first
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleOverride}
                      data-testid="bs-dm-override-btn"
                      className="bg-amber-700 hover:bg-amber-800 text-white"
                    >
                      Send anyway (override)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Similarity-low (non-blocking) */}
        {warnings.includes('similarity_low') && (
          <div
            className="rounded-sm border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600"
            data-testid="bs-similarity-warning"
          >
            <span className="font-semibold text-stone-800">Note:</span> The two drafts are tonally
            close. Consider regenerating for stronger contrast.
          </div>
        )}

        {/* ============ Email Cards (side-by-side) ============ */}
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#B89B5E]" />
          <h2 className="text-sm font-semibold text-stone-800">Outreach drafts · dual tone</h2>
        </div>
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-4"
          data-testid="bs-dual-tone-grid"
        >
          {data.version_a && (
            <ToneCard
              version={data.version_a}
              testidPrefix="bs-version-a"
              locked={locked}
              ctaUrl={data.version_a.cta_url || data.public_deck_url}
              onUse={handleUse}
              onCopy={(p) => handleCopy(p, 'a')}
              copied={copiedKey === 'a'}
            />
          )}
          {data.version_b && (
            <ToneCard
              version={data.version_b}
              testidPrefix="bs-version-b"
              locked={locked}
              ctaUrl={data.version_b.cta_url || data.public_deck_url}
              onUse={handleUse}
              onCopy={(p) => handleCopy(p, 'b')}
              copied={copiedKey === 'b'}
            />
          )}
        </div>

        {/* ============ Footer disclosure ============ */}
        <div
          className="flex items-start gap-2 pt-2 pb-8 text-[11px] text-stone-500 border-t border-stone-100 mt-2 px-1"
          data-testid="bs-footer-disclosure"
        >
          <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[#B89B5E]" />
          <span>
            Draft only — review and edit before sending. The deck is saved as a draft and the
            public URL only resolves after you publish from the editor. The Send buttons open the
            Email Designer; nothing is sent automatically.
          </span>
        </div>

        {isMock && (
          <div className="text-center pb-6 -mt-6">
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 border-amber-300 bg-amber-50"
            >
              Static preview · ?mock=1
            </Badge>
            <button
              type="button"
              onClick={() => handleUse({ ...MOCK_WORKFLOW.version_a })}
              className="hidden"
              aria-hidden="true"
            >
              prefetch
            </button>
            <RefreshCw className="hidden" aria-hidden="true" />
          </div>
        )}
      </main>
    </div>
  );
}
